<template>
  <div class="ss-dl" role="table" :aria-label="tableLabel">
    <div v-if="!ctl.isPhone" class="ss-dh" :class="{ 'is-guest': guest }" role="row">
      <span role="columnheader">{{ $t("day") }}</span>
      <template v-if="guest">
        <span role="columnheader">{{ $t("settings_shell.dl.delivery_from") }}</span>
        <span role="columnheader">{{ $t("settings_shell.dl.delivery_to") }}</span>
        <span role="columnheader">{{ $t("first_day_of_order") }}</span>
        <span role="columnheader">{{ $t("limit") }}</span>
        <span role="columnheader">{{ $t("day_after_limit") }}</span>
      </template>
      <template v-else>
        <span role="columnheader">{{ $t("settings_shell.dl.before") }}</span>
        <span role="columnheader">{{ $t("settings_shell.dl.deadline") }}</span>
        <span role="columnheader">{{ $t("settings_shell.dl.after") }}</span>
        <span role="columnheader">{{ $t("settings_shell.dl.reach") }}</span>
      </template>
    </div>

    <template v-for="(day, i) in days">
      <!-- računar: red tabele -->
      <div
        v-if="!ctl.isPhone"
        :key="day"
        class="ss-drow"
        :class="{ 'is-guest': guest, 'is-sel': selected === i }"
        role="row"
        :data-day="`${kind}:${i}`"
        @click="select(i)"
        @focusin="select(i)"
      >
        <div class="ss-drow__d" role="cell">
          <b>{{ $t(day) }}</b>
        </div>
        <template v-if="guest">
          <set-time role="cell" :path="`${base(day)}.from`" :aria="`${$t(day)}: ${$t('settings_shell.dl.delivery_from')}`" />
          <set-time role="cell" :path="`${base(day)}.to`" :aria="`${$t(day)}: ${$t('settings_shell.dl.delivery_to')}`" />
          <set-stepper role="cell" :path="`${base(day)}.day_before_limit`" :aria="`${$t(day)}: ${$t('first_day_of_order')}`" />
          <set-time role="cell" :path="`${base(day)}.time`" :aria="`${$t(day)}: ${$t('limit')}`" />
          <set-stepper role="cell" :path="`${base(day)}.day_after_limit`" :aria="`${$t(day)}: ${$t('day_after_limit')}`" />
        </template>
        <template v-else>
          <set-stepper role="cell" :path="`${base(day)}.day_before_limit`" :aria="`${$t(day)}: ${$t('settings_shell.dl.before')}`" />
          <set-time role="cell" :path="`${base(day)}.time`" :aria="`${$t(day)}: ${$t('settings_shell.dl.deadline')}`" />
          <set-stepper role="cell" :path="`${base(day)}.day_after_limit`" :aria="`${$t(day)}: ${$t('settings_shell.dl.after')}`" />
          <div class="ss-drow__r" role="cell">
            <set-reach :before="rule(day).day_before_limit" :after="rule(day).day_after_limit" />
          </div>
        </template>
      </div>

      <!-- telefon: kartica po danu -->
      <div v-else :key="day" class="ss-dcard" :class="{ 'is-open': opened === i }" :data-day="`${kind}:${i}`">
        <button
          type="button"
          class="ss-dcard__h"
          :aria-expanded="String(opened === i)"
          :aria-controls="`ss-dcard-${kind}-${i}`"
          @click="toggleCard(i)"
        >
          <b>{{ $t(day) }}</b>
          <span class="ss-dcard__s">{{ rowSummary(day) }}</span>
          <v-icon size="18" class="ss-dcard__c">mdi-chevron-down</v-icon>
        </button>
        <div v-if="opened === i" :id="`ss-dcard-${kind}-${i}`" class="ss-dcard__b">
          <div v-if="guest" class="ss-dcard__r2">
            <set-time :path="`${base(day)}.from`" :label="$t('settings_shell.dl.delivery_from')" :aria="`${$t(day)}: ${$t('settings_shell.dl.delivery_from')}`" />
            <set-time :path="`${base(day)}.to`" :label="$t('settings_shell.dl.delivery_to')" :aria="`${$t(day)}: ${$t('settings_shell.dl.delivery_to')}`" />
          </div>
          <div class="ss-dcard__r2">
            <set-stepper
              :path="`${base(day)}.day_before_limit`"
              :label="guest ? $t('first_day_of_order') : $t('settings_shell.dl.before')"
              :aria="`${$t(day)}: ${guest ? $t('first_day_of_order') : $t('settings_shell.dl.before')}`"
              :unit="$t('settings_shell.dl.days_unit')"
            />
            <set-time
              :path="`${base(day)}.time`"
              :label="guest ? $t('limit') : $t('settings_shell.dl.deadline')"
              :aria="`${$t(day)}: ${guest ? $t('limit') : $t('settings_shell.dl.deadline')}`"
            />
          </div>
          <set-stepper
            :path="`${base(day)}.day_after_limit`"
            :label="guest ? $t('day_after_limit') : $t('settings_shell.dl.after')"
            :aria="`${$t(day)}: ${guest ? $t('day_after_limit') : $t('settings_shell.dl.after')}`"
            :unit="$t('settings_shell.dl.days_unit')"
          />
          <p v-if="!guest" class="ss-dcard__w">
            <v-icon size="15">mdi-calendar-blank-outline</v-icon>
            <span>{{ reachLine(i, day) }}</span>
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { DAYS } from "../settingsLogic";
import SetTime from "./SetTime.vue";
import SetStepper from "./SetStepper.vue";
import SetReach from "./SetReach.vue";

/**
 * Rokovi po danima: na računaru red tabele po danu (izbor reda pokreće kalendar "Šta radnik vidi"),
 * na telefonu kartica po danu koja se otvara. `kind`: "days" (firme) ili "guest_day" (gosti).
 */
export default {
  name: "SetDayRows",
  components: { SetTime, SetStepper, SetReach },
  inject: { ctl: "settingsCtl" },
  props: {
    kind: { type: String, required: true },
  },
  data: () => ({ days: DAYS }),
  computed: {
    guest() {
      return this.kind === "guest_day";
    },
    tableLabel() {
      return this.$t(this.guest ? "settings_shell.offer.days_table" : "settings_shell.dl.table");
    },
    selected() {
      return this.ctl.ui.sim[this.kind].day;
    },
    opened() {
      return this.ctl.ui.open[this.kind];
    },
  },
  methods: {
    base(day) {
      return `${this.kind}.${day}`;
    },
    rule(day) {
      return this.ctl.form[this.kind][day];
    },
    select(index) {
      if (this.selected !== index) {
        this.ctl.ui.sim[this.kind].day = index;
      }
    },
    toggleCard(index) {
      this.ctl.ui.open[this.kind] = this.opened === index ? -1 : index;
      this.select(index);
    },
    rowSummary(day) {
      const rule = this.rule(day);
      const time = rule.time || "—";
      if (this.guest) {
        return this.$t("settings_shell.dl.sum_guest", {
          from: rule.from || "—",
          to: rule.to || "—",
          time,
          before: rule.day_before_limit === "" ? "—" : rule.day_before_limit,
          after: rule.day_after_limit === "" ? "—" : rule.day_after_limit,
        });
      }
      return this.$t("settings_shell.dl.sum_worker", {
        time,
        before: rule.day_before_limit === "" ? 0 : rule.day_before_limit,
        after: this.ctl.daysLabel(rule.day_after_limit),
      });
    },
    dateFor(weekdayIndex, ahead) {
      const start = new Date();
      start.setHours(12, 0, 0, 0);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7) + weekdayIndex + (Number(ahead) || 0));
      return this.localeDate(start, { weekday: "short", day: "numeric", month: "short" });
    },
    reachLine(index, day) {
      const rule = this.rule(day);
      return this.$t("settings_shell.dl.reach_line", {
        before: this.dateFor(index, rule.day_before_limit),
        after: this.dateFor(index, rule.day_after_limit),
      });
    },
  },
};
</script>
